import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

export interface Customer {
  id: string;
  tenant_id: string;
  name: string;
  mobile: string;
  aadhaar_last_four: string | null;
  address: string | null;
  notes: string | null;
  total_visits: number;
  total_spent: number;
  total_due: number;
  created_at: string;
  updated_at: string;
}

export interface CustomerInput {
  name: string;
  mobile: string;
  aadhaar_last_four?: string | null;
  address?: string | null;
  notes?: string | null;
}

export function useCustomers() {
  const { user, tenant } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: customers,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["customers", tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("tenant_id", tenant.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!user && !!tenant?.id,
  });

  const searchCustomerByMobile = async (mobile: string): Promise<Customer | null> => {
    if (!tenant?.id || !mobile || mobile.length < 10) return null;

    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("tenant_id", tenant.id)
      .eq("mobile", mobile)
      .maybeSingle();

    if (error) {
      console.error("Error searching customer:", error);
      return null;
    }
    return data as Customer | null;
  };

  const searchCustomersByName = async (query: string): Promise<Customer[]> => {
    if (!tenant?.id || !query || query.trim().length < 1) return [];

    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("tenant_id", tenant.id)
      .or(`name.ilike.%${query}%,mobile.ilike.%${query}%`)
      .order("name", { ascending: true })
      .limit(10);

    if (error) {
      console.error("Error searching customers:", error);
      return [];
    }
    return data as Customer[];
  };

  const createCustomer = async (input: CustomerInput): Promise<Customer> => {
    if (!user || !tenant?.id) {
      throw new Error("User must be logged in");
    }

    const { data, error } = await supabase
      .from("customers")
      .insert({
        tenant_id: tenant.id,
        name: input.name,
        mobile: input.mobile,
        aadhaar_last_four: input.aadhaar_last_four || null,
        address: input.address || null,
        notes: input.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ["customers", tenant.id] });
    return data as Customer;
  };

  const updateCustomer = async (id: string, input: Partial<CustomerInput>): Promise<Customer> => {
    if (!tenant?.id) {
      throw new Error("User must be logged in");
    }

    const { data, error } = await supabase
      .from("customers")
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("tenant_id", tenant.id)
      .select()
      .single();

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ["customers", tenant.id] });
    queryClient.invalidateQueries({ queryKey: ["customer", id] });
    return data as Customer;
  };

  const deleteCustomer = async (id: string): Promise<void> => {
    if (!tenant?.id) {
      throw new Error("User must be logged in");
    }

    const { error } = await supabase
      .from("customers")
      .delete()
      .eq("id", id)
      .eq("tenant_id", tenant.id);

    if (error) throw error;
    
    queryClient.invalidateQueries({ queryKey: ["customers", tenant.id] });
  };

  const incrementCustomerStats = async (customerId: string, amount: number): Promise<void> => {
    if (!tenant?.id) return;

    // Get current stats
    const { data: customer, error: fetchError } = await supabase
      .from("customers")
      .select("total_visits, total_spent")
      .eq("id", customerId)
      .single();

    if (fetchError || !customer) return;

    // Update stats
    const { error } = await supabase
      .from("customers")
      .update({
        total_visits: (customer.total_visits || 0) + 1,
        total_spent: (customer.total_spent || 0) + amount,
        updated_at: new Date().toISOString(),
      })
      .eq("id", customerId);

    if (error) {
      console.error("Error updating customer stats:", error);
    }

    queryClient.invalidateQueries({ queryKey: ["customers", tenant.id] });
    queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
  };

  return {
    customers,
    isLoading,
    error,
    refetch,
    searchCustomerByMobile,
    searchCustomersByName,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    incrementCustomerStats,
  };
}

export function useCustomer(customerId: string | undefined) {
  const { user, tenant } = useAuth();

  return useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      if (!customerId || !tenant?.id) return null;

      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", customerId)
        .eq("tenant_id", tenant.id)
        .maybeSingle();

      if (error) throw error;
      return data as Customer | null;
    },
    enabled: !!user && !!tenant?.id && !!customerId,
  });
}

export function useCustomerSales(customerId: string | undefined) {
  const { user, tenant } = useAuth();

  return useQuery({
    queryKey: ["customer-sales", customerId],
    queryFn: async () => {
      if (!customerId || !tenant?.id) return [];

      const { data, error } = await supabase
        .from("sales")
        .select(`
          id,
          invoice_number,
          total_amount,
          payment_status,
          created_at,
          sale_items (
            service_name,
            quantity,
            total_price
          )
        `)
        .eq("customer_id", customerId)
        .eq("tenant_id", tenant.id)
        .eq("is_deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!tenant?.id && !!customerId,
  });
}
